import React from 'react';
import { Badge, Button, Drawer, Input } from 'antd';
import { FilterOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { TOUCH_TARGET } from '../../constants/mobile';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterGroup {
  key: string;
  label: string;
  options: FilterOption[];
  value: string;
  /** The value that means "not filtering". Only other values raise a chip. */
  allValue?: string;
  onChange: (value: string) => void;
}

/**
 * Search plus filters for a phone.
 *
 * A column of full-width Selects is the desktop bar with its flex-direction flipped: it
 * costs a screen of height to say nothing, since most of the time every filter is at "All".
 * Here one row holds the search and a filter button carrying a count, the sheet behind it
 * holds the choices as tappable chips, and what is actually filtering is the only thing
 * that takes space on the screen — as removable chips under the search.
 */
const MobileFilterBar: React.FC<{
  search?: { value: string; placeholder?: string; onChange: (value: string) => void };
  groups?: FilterGroup[];
  /** Rendered at the end of the top row, e.g. a refresh button. */
  extra?: React.ReactNode;
}> = ({ search, groups = [], extra }) => {
  const [open, setOpen] = React.useState(false);

  const isActive = (g: FilterGroup) => g.value !== (g.allValue ?? 'all') && g.value != null;
  const active = groups.filter(isActive);

  const labelOf = (g: FilterGroup) =>
    g.options.find((o) => o.value === g.value)?.label ?? g.value;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {search && (
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: '#5E6673' }} />}
            placeholder={search.placeholder}
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            style={{ flex: 1, minWidth: 0, height: TOUCH_TARGET }}
          />
        )}
        {groups.length > 0 && (
          <Badge count={active.length} size="small" offset={[-2, 2]}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setOpen(true)}
              aria-label="Filters"
              style={{
                height: TOUCH_TARGET,
                // Square only when it sits beside a search field, where the input carries
                // the meaning. On its own a bare icon box reads as a stray element, so it
                // takes its label and sizes to it.
                ...(search ? { width: TOUCH_TARGET } : null),
                borderColor: active.length ? '#0ECB81' : undefined,
                color: active.length ? '#0ECB81' : undefined,
                flexShrink: 0,
              }}
            >
              {search ? null : 'Filters'}
            </Button>
          </Badge>
        )}
        {extra}
      </div>

      {active.length > 0 && (
        <div
          style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }}
          aria-label="Active filters"
        >
          {active.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => g.onChange(g.allValue ?? 'all')}
              style={chipStyle(true)}
            >
              {labelOf(g)}
              <CloseOutlined style={{ fontSize: 9 }} />
            </button>
          ))}
        </div>
      )}

      <Drawer
        placement="bottom"
        height="auto"
        open={open}
        onClose={() => setOpen(false)}
        title="Filters"
        styles={{ body: { padding: '4px 16px calc(16px + env(safe-area-inset-bottom))' } }}
        extra={
          <Button
            type="text"
            size="small"
            disabled={active.length === 0}
            onClick={() => groups.forEach((g) => g.onChange(g.allValue ?? 'all'))}
            style={{ color: active.length ? '#0ECB81' : undefined }}
          >
            Reset
          </Button>
        }
      >
        {groups.map((g) => (
          <div key={g.key} style={{ marginBottom: 18 }}>
            <div style={{ color: '#848E9C', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>
              {g.label}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {g.options.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  aria-pressed={o.value === g.value}
                  onClick={() => g.onChange(o.value)}
                  style={chipStyle(o.value === g.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        ))}
        <Button block type="primary" onClick={() => setOpen(false)} style={{ height: TOUCH_TARGET }}>
          Done
        </Button>
      </Drawer>
    </div>
  );
};

const chipStyle = (selected: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 32,
  padding: '0 12px',
  borderRadius: 16,
  fontSize: 12.5,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  background: selected ? 'rgba(14, 203, 129, 0.15)' : '#1E2329',
  color: selected ? '#0ECB81' : '#B7BDC6',
  border: `1px solid ${selected ? '#0ECB81' : '#2B3139'}`,
  WebkitTapHighlightColor: 'transparent',
});

export default MobileFilterBar;
